"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted animate-pulse rounded-none" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-none" />
        <div className="h-9 w-20 bg-muted animate-pulse rounded-none" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground hover:text-primary focus:text-primary">
              <Link to={item.href || "#"} className="group relative px-4 py-2 font-mono text-xs uppercase tracking-widest text-foreground transition-colors">
                {item.label}
                <span className="absolute bottom-1 left-4 right-4 h-[1px] origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-10 w-full bg-muted animate-pulse rounded-none" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-none" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-none" />
      </div>;
  }
  return <div className="flex flex-col py-4 bg-background">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block px-6 py-4 font-mono text-sm uppercase tracking-widest text-foreground hover:bg-secondary hover:text-primary transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="px-6 py-4 mt-2 border-t border-border">
        <Button asChild className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90" size="lg">
          <Link to="/StartProjectPage" onClick={onItemClick}>
            Start a Project
          </Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <section data-section-id="222854" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-in-out ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-none" : "bg-background border-b border-border/20"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/pivotal_placement_logo.png" alt="Pivotal Placement" className="h-6 w-auto object-contain brightness-100" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="41">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex flex-shrink-0 items-center">
            <Button asChild variant="default" className="font-mono text-xs uppercase tracking-widest rounded-none bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300">
              <Link to="/StartProjectPage">Start a Project</Link>
            </Button>
          </div>

          {}
          <div className="flex md:hidden">
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu} aria-label="Toggle navigation menu" className="text-foreground hover:bg-secondary/50 rounded-none">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className={`absolute left-0 top-full w-full origin-top bg-background border-b border-border transition-all duration-500 ease-in-out md:hidden ${isMobileMenuOpen ? "opacity-100 scale-y-100 visible" : "opacity-0 scale-y-0 invisible"}`}>
        <MenuProvider menu_id="41">
          <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}